import {DataFetcher} from '@aboutyou/data-fetcher';
import {
  GetProductDetailsBulkResponse,
  ReviewsSection_ReviewSummary,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  useTrackFeature,
  useTrackFeatureInViewImpression,
} from '@aboutyou/tracking';
import {
  Badge,
  COLORS,
  FONT_SIZES,
  GenericIconName,
  TabContentItem,
  TabLabel,
} from '@aboutyou/ui-components';
import React, {FC} from 'react';
import styled from 'styled-components';
import {ProductDetailsReviewsSectionView} from '../../ReviewsSection/ProductDetailsReviewsSection/ProductDetailsReviewsSectionView';
import {useProductDetailsReviewsABTest} from '../../ReviewsSection/utils/useReviewsABTest';
import {useRatingsAndReviewsSection} from '../useGetProductDetailsBulkSection';
import {LoadingTab} from './LoadingTab';
import {useProductReviewSectionScrollManager} from './useProductReviewSectionScrollManager';

export const ReviewsSectionTab = () => {
  return (
    <DataFetcher
      request={useRatingsAndReviewsSection()}
      Error={() => null}
      Loading={() => <LoadingTab name={REVIEWS_SECTION_NAME} />}
      View={ReviewsSectionTabView}
    />
  );
};

export const REVIEWS_SECTION_NAME = 'REVIEWS_SECTION';

const ReviewsSectionTabView: FC<
  GetProductDetailsBulkResponse['reviewSection']
> = ({summary}) => {
  const {showReviewsOnProductDetails} = useProductDetailsReviewsABTest(
    Boolean(summary),
  );

  if (!showReviewsOnProductDetails || !summary) {
    return null;
  }

  return <TrackedTabContentItem {...summary} />;
};

const TrackedTabContentItem: FC<ReviewsSection_ReviewSummary> = props => {
  const {t} = useTranslation();

  const featureTrackingLabel = 'review|adp|section|';

  const {ref} = useTrackFeatureInViewImpression<HTMLButtonElement>({
    label: `${featureTrackingLabel}${props.overallRating}|${props.reviewCount}`,
  });

  const trackFeature = useTrackFeature();

  const onClickLabel = () => {
    trackFeature({
      eventType: 'click',
      label: `${featureTrackingLabel}${props.overallRating}|${props.reviewCount}`,
    });
  };

  return (
    <StyledTabContentItem
      name={REVIEWS_SECTION_NAME}
      label={({isActive}) => (
        <LabelWrapper>
          {t('Customer reviews')}
          <StyledBadge
            text={String(props.overallRating)}
            backgroundColor={isActive ? COLORS.Black : COLORS.Grey4}
            svgIcon={{
              iconName: GenericIconName.StarFilled,
              iconSize: 'XSS',
              fill: isActive ? COLORS.White : COLORS.Grey1,
            }}
            textColor={isActive ? COLORS.White : COLORS.Grey1}
          />
        </LabelWrapper>
      )}
      content={<ProductDetailsReviewsSectionView {...props} />}
      ref={ref}
      onClickLabel={onClickLabel}
    />
  );
};

export const ScrollReviewsSectionIntoView: React.FC<
  React.PropsWithChildren
> = () => {
  const {ScrollToTarget} = useProductReviewSectionScrollManager();
  return <ScrollToTarget />;
};

const LabelWrapper = styled.span`
  align-items: center;
  display: flex;
  gap: 0.5rem;
`;

const StyledBadge = styled(Badge)`
  border-radius: 0.7rem;
  font-size: ${FONT_SIZES.px10};
  gap: 0.25rem;
  padding: 0.5rem;
  transition: background-color 200ms ease, color 200ms ease;
`;

const StyledTabContentItem = styled(TabContentItem)`
  ${TabLabel}:hover ${StyledBadge} {
    background-color: ${COLORS.Black};
    color: ${COLORS.White};
  }

  ${TabLabel} ${StyledBadge} svg {
    transition: fill 200ms ease;
  }

  ${TabLabel}:hover ${StyledBadge} svg {
    fill: ${COLORS.White};
  }
`;
